import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import toast, { Toaster } from 'react-hot-toast';
import {
  ClipboardCheck,
  Download,
  Search,
  Loader2,
  ChevronDown,
  ChevronUp,
  Mail,
  MapPin,
  ExternalLink,
  CheckCircle,
  Clock,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { TradeshowOrderService, formatDisplaySummary } from '../../services/TradeshowOrderService';
import { TradeshowOrderRecord } from '../../@types/tradeshowOrder';
import { CSVExportService } from '../../services/CSVExportService';
import { formatOrderDateForDisplay } from '../../utils/calendarDate';
import {
  Card,
  Button,
  Heading2,
  Text,
  PageContainer,
  MainContent,
} from '../../styles/StyledComponents';
import {
  ThemeColors,
  Spacing,
  Typography,
  BorderRadius,
} from '../../styles/ThemeStyles';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.xl};
  flex-wrap: wrap;
  gap: ${Spacing.md};
`;

const PageTitle = styled.h1`
  font-family: ${Typography.fontFamily.secondary};
  font-size: 1.6rem;
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  margin: 0;

  svg {
    color: ${ThemeColors.primary.main};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  flex-wrap: wrap;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.xl};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(Card)<{ accentColor?: string }>`
  padding: ${Spacing.lg};
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  border-left: 4px solid ${(props) => props.accentColor || ThemeColors.primary.main};
`;

const StatValue = styled.span`
  font-size: 1.6rem;
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  display: block;
`;

const StatLabel = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const ToolbarCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  padding: ${Spacing.md} ${Spacing.lg};
  margin-bottom: ${Spacing.lg};
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  flex: 1;
  min-width: 200px;
  background: ${ThemeColors.gray[50]};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.xs} ${Spacing.md};

  input {
    border: none;
    background: transparent;
    outline: none;
    font-size: ${Typography.fontSize.sm};
    color: ${ThemeColors.text.primary};
    width: 100%;
    padding: ${Spacing.xs} 0;
  }

  svg {
    color: ${ThemeColors.text.secondary};
    flex-shrink: 0;
  }
`;

const TableCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.secondary};
  background: ${ThemeColors.gray[50]};
  border-bottom: 2px solid ${ThemeColors.border.light};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  border-bottom: 1px solid ${ThemeColors.border.light};
  vertical-align: top;
`;

const Tr = styled.tr`
  cursor: pointer;

  &:hover {
    background: ${ThemeColors.gray[50]};
  }
`;

const Badge = styled.span<{ $tone: 'new' | 'done' | 'mail' | 'warn' }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  background: ${(p) =>
    p.$tone === 'done'
      ? `${ThemeColors.success.main}20`
      : p.$tone === 'mail'
        ? `${ThemeColors.info.main}20`
        : p.$tone === 'warn'
          ? `${ThemeColors.warning.main}30`
          : `${ThemeColors.secondary.main}18`};
  color: ${(p) =>
    p.$tone === 'done'
      ? ThemeColors.success.dark
      : p.$tone === 'mail'
        ? ThemeColors.info.dark
        : p.$tone === 'warn'
          ? ThemeColors.warning.dark
          : ThemeColors.secondary.dark};
`;

const DetailPanel = styled.div`
  background: ${ThemeColors.gray[50]};
  padding: ${Spacing.lg} ${Spacing.xl};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.md} ${Spacing.xl};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: ${Spacing.md};
  }
`;

const DetailLabel = styled.div`
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${Spacing.xs};
`;

const DetailValue = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  background: white;
  padding: ${Spacing.sm} ${Spacing.md};
  border-radius: ${BorderRadius.md};
  border: 1px solid ${ThemeColors.border.light};
  word-break: break-word;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxl};
  color: ${ThemeColors.text.secondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.md};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xxl} 0;
  gap: ${Spacing.md};
  color: ${ThemeColors.text.secondary};

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatWhen(value: unknown): string {
  const date = toDate(value);
  if (!date) return '—';
  return date.toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const TradeshowSubmissionsScreen: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState<TradeshowOrderRecord[]>([]);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await TradeshowOrderService.getAllSubmissions();
      setSubmissions(data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load trade show submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return submissions;
    return submissions.filter((row) =>
      [
        row.storeName,
        row.storeNumber,
        row.storeAddress,
        row.contactName,
        row.email,
        row.poNumber,
        row.accountManagerNotes,
        formatDisplaySummary(row.displays, row.displayPanels),
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [submissions, search]);

  const stats = useMemo(() => {
    const total = submissions.length;
    const newCount = submissions.filter((s) => s.status !== 'reviewed').length;
    const reviewed = submissions.filter((s) => s.status === 'reviewed').length;
    const emailed = submissions.filter((s) => s.emailSent).length;
    return { total, newCount, reviewed, emailed };
  }, [submissions]);

  const handleExport = async () => {
    if (filtered.length === 0) {
      toast.error('No submissions to export');
      return;
    }
    try {
      const data = filtered.map((row) => ({
        'Submitted': formatWhen(row.createdAt),
        'Status': row.status,
        'Store Name': row.storeName,
        'Store #': row.storeNumber,
        'Address': row.storeAddress,
        'Contact': row.contactName,
        'Email': row.email,
        'PO #': row.poNumber || '',
        'Delivery Date': row.deliveryDate ? formatOrderDateForDisplay(row.deliveryDate) : '',
        'Display': formatDisplaySummary(row.displays, row.displayPanels),
        'Notes for Account Manager': row.accountManagerNotes || '',
        'Email Sent': row.emailSent ? 'Yes' : 'No',
      }));
      await CSVExportService.exportDataToCSV(data, {
        filename: `tradeshow_orders_${new Date().toISOString().split('T')[0]}.csv`,
      });
      toast.success('CSV exported');
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  const toggleReviewed = async (row: TradeshowOrderRecord) => {
    try {
      if (row.status === 'reviewed') {
        await TradeshowOrderService.markSubmitted(row.id);
        toast.success('Marked as new');
      } else {
        await TradeshowOrderService.markReviewed(row.id, user?.email || user?.username || 'manager');
        toast.success('Marked as reviewed');
      }
      await load();
    } catch {
      toast.error('Could not update status');
    }
  };

  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
        <Toaster position="top-right" />
        <PageHeader>
          <PageTitle>
            <ClipboardCheck size={28} />
            Trade Show Submissions
          </PageTitle>
          <HeaderActions>
            <Button type="button" onClick={() => window.open('/tradeshow', '_blank', 'noopener')}>
              <ExternalLink size={16} /> Open form
            </Button>
            <Button onClick={handleExport}>
              <Download size={16} /> Export CSV
            </Button>
          </HeaderActions>
        </PageHeader>

        <StatsGrid>
          <StatCard accentColor={ThemeColors.primary.main}>
            <div>
              <StatValue>{stats.total}</StatValue>
              <StatLabel>Total orders</StatLabel>
            </div>
          </StatCard>
          <StatCard accentColor={ThemeColors.secondary.main}>
            <div>
              <StatValue>{stats.newCount}</StatValue>
              <StatLabel>Needs review</StatLabel>
            </div>
          </StatCard>
          <StatCard accentColor={ThemeColors.success.main}>
            <div>
              <StatValue>{stats.reviewed}</StatValue>
              <StatLabel>Reviewed</StatLabel>
            </div>
          </StatCard>
          <StatCard accentColor={ThemeColors.info.main}>
            <div>
              <StatValue>{stats.emailed}</StatValue>
              <StatLabel>Emailed to Caroline</StatLabel>
            </div>
          </StatCard>
        </StatsGrid>

        <ToolbarCard>
          <SearchInput>
            <Search size={16} />
            <input
              placeholder="Search store, contact, email, PO…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && <X size={16} style={{ cursor: 'pointer' }} onClick={() => setSearch('')} />}
          </SearchInput>
          <Text style={{ margin: 0 }}>
            {filtered.length} of {submissions.length}
          </Text>
        </ToolbarCard>

        {loading ? (
          <LoadingContainer>
            <Loader2 size={32} />
            Loading submissions…
          </LoadingContainer>
        ) : submissions.length === 0 ? (
          <EmptyState>
            <ClipboardCheck size={48} />
            <Heading2 style={{ color: ThemeColors.text.secondary }}>No submissions yet</Heading2>
            <Text>Orders from the public trade show form will appear here.</Text>
          </EmptyState>
        ) : (
          <TableCard>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Submitted</Th>
                    <Th>Store</Th>
                    <Th>Contact</Th>
                    <Th>Display</Th>
                    <Th>Status</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => {
                    const open = expandedId === row.id;
                    return (
                      <React.Fragment key={row.id}>
                        <Tr onClick={() => setExpandedId(open ? null : row.id)}>
                          <Td>{formatWhen(row.createdAt)}</Td>
                          <Td>
                            <strong>{row.storeName}</strong>
                            <div style={{ color: ThemeColors.text.secondary }}>#{row.storeNumber}</div>
                          </Td>
                          <Td>
                            {row.contactName}
                            <div style={{ color: ThemeColors.text.secondary }}>{row.email}</div>
                          </Td>
                          <Td>{formatDisplaySummary(row.displays, row.displayPanels) || '—'}</Td>
                          <Td>
                            <Badge $tone={row.status === 'reviewed' ? 'done' : 'new'}>
                              {row.status === 'reviewed' ? 'Reviewed' : 'New'}
                            </Badge>
                            {' '}
                            <Badge $tone={row.emailSent ? 'mail' : 'warn'}>
                              {row.emailSent ? 'Emailed' : 'Email failed'}
                            </Badge>
                          </Td>
                          <Td>
                            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </Td>
                        </Tr>
                        {open && (
                          <tr>
                            <td colSpan={6} style={{ padding: 0 }}>
                              <DetailPanel>
                                <div>
                                  <DetailLabel><MapPin size={12} /> Address</DetailLabel>
                                  <DetailValue>{row.storeAddress}</DetailValue>
                                </div>
                                <div>
                                  <DetailLabel><Mail size={12} /> Email</DetailLabel>
                                  <DetailValue>
                                    <a href={`mailto:${row.email}`}>{row.email}</a>
                                  </DetailValue>
                                </div>
                                <div>
                                  <DetailLabel>PO #</DetailLabel>
                                  <DetailValue>{row.poNumber || '—'}</DetailValue>
                                </div>
                                <div>
                                  <DetailLabel>Delivery date</DetailLabel>
                                  <DetailValue>
                                    {row.deliveryDate ? formatOrderDateForDisplay(row.deliveryDate) : '—'}
                                  </DetailValue>
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <DetailLabel>Displays</DetailLabel>
                                  <DetailValue>
                                    {formatDisplaySummary(row.displays, row.displayPanels) || '—'}
                                  </DetailValue>
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <DetailLabel>Notes for account manager</DetailLabel>
                                  <DetailValue style={{ whiteSpace: 'pre-wrap' }}>
                                    {row.accountManagerNotes || '—'}
                                  </DetailValue>
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <Button type="button" onClick={() => toggleReviewed(row)}>
                                    {row.status === 'reviewed' ? (
                                      <><Clock size={16} /> Mark as new</>
                                    ) : (
                                      <><CheckCircle size={16} /> Mark as reviewed</>
                                    )}
                                  </Button>
                                </div>
                              </DetailPanel>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          </TableCard>
        )}
      </MainContent>
    </PageContainer>
  );
};
